import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  BLOG_SITE_DESCRIPTION,
  BLOG_SITE_TITLE,
  BLOG_ROOT_PATH,
  buildBlogPostPath,
  getBlogPostBySlug,
  getSortedBlogPosts,
} from "../content/blogPosts";
import { DOCS_PAGE, DOCS_ROOT_PATH } from "../content/docsPages";

const SITE_NAME = "Latted";
const SITE_URL = "https://latted.com";
const DEFAULT_IMAGE_URL = `${SITE_URL}/latted_social_preview_dark.png`;

function upsertMeta(name, content, attr = "name") {
  const selector = `meta[${attr}="${name}"]`;
  let element = document.head.querySelector(selector);

  if (!content) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!href) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function normalizePath(pathname) {
  if (!pathname || pathname === "/") {
    return "/";
  }
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function getBlogSlug(pathname) {
  const normalized = normalizePath(pathname);
  if (!normalized.startsWith(`${BLOG_ROOT_PATH}/`)) {
    return "";
  }
  return normalized.replace(`${BLOG_ROOT_PATH}/`, "");
}

function getRouteSeo(pathname) {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === "/academic-papers") {
    return {
      title: "Academic Papers to Video - Latted",
      description:
        "Upload an academic paper PDF, choose the audience and runtime, and let Latted turn it into a polished video explainer.",
      canonicalPath: "/academic-papers",
      robots: "index, follow",
      includeStructuredData: true,
    };
  }

  if (normalizedPath === "/pricing") {
    return {
      title: "Latted Pricing - Pay-as-you-go AI Video Generation",
      description:
        "Transparent pay-as-you-go pricing for AI video generation. Compare model rates, estimate costs, and only pay for what you render with Latted.",
      canonicalPath: "/pricing",
      robots: "index, follow",
      includeStructuredData: true,
      ogType: "website",
      pageType: "marketing",
    };
  }

  if (normalizedPath === "/privacy-policy") {
    return {
      title: "Privacy Policy - Latted",
      description:
        "Learn how Latted collects, uses, and protects your information when you use our AI video creation platform.",
      canonicalPath: "/privacy-policy",
      robots: "index, follow",
      includeStructuredData: false,
      ogType: "website",
      pageType: "marketing",
    };
  }

  if (normalizedPath === "/terms-of-service") {
    return {
      title: "Terms of Service - Latted",
      description:
        "Read the terms and conditions that govern your use of Latted's AI video creation platform.",
      canonicalPath: "/terms-of-service",
      robots: "index, follow",
      includeStructuredData: false,
      ogType: "website",
      pageType: "marketing",
    };
  }

  if (normalizedPath === BLOG_ROOT_PATH) {
    return {
      title: "Latted Blog - AI Video Tutorials and Workflow Guides",
      description: BLOG_SITE_DESCRIPTION,
      canonicalPath: BLOG_ROOT_PATH,
      robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
      includeStructuredData: true,
      ogType: "website",
      pageType: "blog-index",
      ogImage: DEFAULT_IMAGE_URL,
      keywords: [
        "AI video blog",
        "AI video tutorials",
        "Latted blog",
        "AI video workflow guides",
        "Seedance tutorials",
        "creator workflow guides",
      ],
    };
  }

  if (normalizedPath === DOCS_ROOT_PATH) {
    return {
      title: DOCS_PAGE.seoTitle || DOCS_PAGE.title,
      description: DOCS_PAGE.description,
      canonicalPath: DOCS_ROOT_PATH,
      robots:
        "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
      includeStructuredData: true,
      ogType: "article",
      pageType: "docs",
      keywords: DOCS_PAGE.keywords,
      authorName: "Latted Team",
      publishedAt: DOCS_PAGE.updatedAt,
      updatedAt: DOCS_PAGE.updatedAt,
      ogImage: DEFAULT_IMAGE_URL,
    };
  }

  if (normalizedPath.startsWith(`${BLOG_ROOT_PATH}/`)) {
    const slug = getBlogSlug(normalizedPath);
    const post = getBlogPostBySlug(slug);

    if (post) {
      return {
        title: post.seoTitle || post.title,
        description: post.description,
        canonicalPath: buildBlogPostPath(post.slug),
        robots:
          "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
        includeStructuredData: true,
        ogType: "article",
        pageType: "blog-post",
        post,
        keywords: post.keywords,
        authorName: post.authorName,
        publishedAt: post.publishedAt,
        updatedAt: post.updatedAt,
        ogImage: post.coverImagePath
          ? `${SITE_URL}${post.coverImagePath}`
          : DEFAULT_IMAGE_URL,
      };
    }

    return {
      title: "Latted Blog",
      description:
        "Browse the latest Latted blog articles on AI video generation and Seedance workflows.",
      canonicalPath: normalizedPath,
      robots: "noindex, nofollow",
      includeStructuredData: false,
      ogType: "website",
      pageType: "not-found",
    };
  }

  if (normalizedPath.startsWith("/project/")) {
    return {
      title: "Project Editor - Latted",
      description:
        "Create and edit AI video projects in Latted's timeline editor.",
      canonicalPath: normalizedPath,
      robots: "noindex, nofollow",
      includeStructuredData: false,
      ogType: "website",
      pageType: "product",
    };
  }

  if (
    normalizedPath.startsWith("/account") ||
    normalizedPath.startsWith("/projects") ||
    normalizedPath.startsWith("/onboarding") ||
    normalizedPath.startsWith("/invite") ||
    normalizedPath.startsWith("/collection")
  ) {
    return {
      title: `${SITE_NAME} - AI Video Studio`,
      description:
        "Latted helps you turn ideas into high-quality AI videos in minutes.",
      canonicalPath: normalizedPath,
      robots: "noindex, nofollow",
      includeStructuredData: false,
      ogType: "website",
      pageType: "product",
    };
  }

  return {
    title: "Latted - AI Video Generator & Editor",
    description:
      "Generate, edit, and publish AI videos in one timeline. Latted gives you script-to-video workflows, asset consistency, motion graphics, and audio tools.",
    canonicalPath: "/",
    robots: "index, follow",
    includeStructuredData: true,
    ogType: "website",
    pageType: "marketing",
  };
}

const PUBLISHER_SCHEMA = {
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/favicon.svg`,
  },
};

function buildBaseStructuredData(pathname) {
  const normalizedPath = normalizePath(pathname);
  const pageUrl = `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;

  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.svg`,
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      publisher: PUBLISHER_SCHEMA,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/pricing?query={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: SITE_NAME,
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web",
      url: pageUrl,
      description:
        "AI video generation and editing platform with a unified timeline, asset management, and production-ready outputs.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Pay-as-you-go usage with no monthly seat requirement.",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Latted site sections",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          url: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Academic Papers",
          url: `${SITE_URL}/academic-papers`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Pricing",
          url: `${SITE_URL}/pricing`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "Blog",
          url: `${SITE_URL}/blog`,
        },
        {
          "@type": "ListItem",
          position: 5,
          name: "Documentation",
          url: `${SITE_URL}${DOCS_ROOT_PATH}`,
        },
      ],
    },
  ];
}

function buildBlogIndexStructuredData() {
  const posts = getSortedBlogPosts();
  const blogUrl = `${SITE_URL}/blog`;

  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: BLOG_SITE_TITLE,
      description: BLOG_SITE_DESCRIPTION,
      url: blogUrl,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
      mainEntity: {
        "@type": "Blog",
        name: BLOG_SITE_TITLE,
        url: blogUrl,
      },
      about: [
        {
          "@type": "Thing",
          name: "AI video tutorials",
        },
        {
          "@type": "Thing",
          name: "Prompt engineering",
        },
        {
          "@type": "Thing",
          name: "Creator workflows",
        },
      ],
      inLanguage: "en",
    },
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: BLOG_SITE_TITLE,
      description: BLOG_SITE_DESCRIPTION,
      url: blogUrl,
      publisher: PUBLISHER_SCHEMA,
      inLanguage: "en",
      blogPost: posts.map((post) => ({
        "@type": "BlogPosting",
        headline: post.title,
        url: `${SITE_URL}${buildBlogPostPath(post.slug)}`,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
        author: {
          "@type": "Person",
          name: post.authorName,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Latted Blog Posts",
      numberOfItems: posts.length,
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      itemListElement: posts.map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${SITE_URL}${buildBlogPostPath(post.slug)}`,
        name: post.title,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: blogUrl,
        },
      ],
    },
  ];
}

function estimateWordCount(post) {
  const wordsPerMinute = 230;
  return post.readingTimeMinutes * wordsPerMinute;
}

function buildBlogPostStructuredData(post) {
  const postPath = buildBlogPostPath(post.slug);
  const postUrl = `${SITE_URL}${postPath}`;
  const imageUrl = post.coverImagePath
    ? `${SITE_URL}${post.coverImagePath}`
    : DEFAULT_IMAGE_URL;
  const faqItems = Array.isArray(post.faqItems) ? post.faqItems : [];

  const primaryTag = post.tags?.[0] || "AI Video";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      alternativeHeadline: post.seoTitle || post.title,
      description: post.description,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": postUrl,
      },
      url: postUrl,
      author: {
        "@type": "Person",
        name: post.authorName,
      },
      publisher: PUBLISHER_SCHEMA,
      image: {
        "@type": "ImageObject",
        url: imageUrl,
      },
      inLanguage: "en",
      keywords: (post.keywords || []).join(", "),
      wordCount: estimateWordCount(post),
      articleSection: primaryTag,
      isAccessibleForFree: true,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [
          ".blog-post-page__title",
          ".blog-post-page__description",
        ],
      },
      isPartOf: {
        "@type": "Blog",
        name: "Latted Blog",
        url: `${SITE_URL}/blog`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${SITE_URL}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.title,
          item: postUrl,
        },
      ],
    },
  ];

  if (faqItems.length > 0) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems
        .filter((item) => item?.question && item?.answer)
        .map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
    });
  }

  return structuredData;
}

function buildDocsStructuredData() {
  const docsUrl = `${SITE_URL}${DOCS_ROOT_PATH}`;
  const faqItems = Array.isArray(DOCS_PAGE.faqItems) ? DOCS_PAGE.faqItems : [];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: DOCS_PAGE.title,
      alternativeHeadline: DOCS_PAGE.seoTitle,
      description: DOCS_PAGE.description,
      url: docsUrl,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": docsUrl,
      },
      datePublished: DOCS_PAGE.updatedAt,
      dateModified: DOCS_PAGE.updatedAt,
      author: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      publisher: PUBLISHER_SCHEMA,
      image: DEFAULT_IMAGE_URL,
      inLanguage: "en",
      keywords: (DOCS_PAGE.keywords || []).join(", "),
      articleSection: "Documentation",
      proficiencyLevel: "Beginner to advanced",
      about: [
        { "@type": "Thing", name: "AI video generation" },
        { "@type": "Thing", name: "AI video editing" },
        { "@type": "Thing", name: "UGC ads" },
        { "@type": "Thing", name: "AI video automations" },
      ],
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Documentation",
          item: docsUrl,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "Create your first AI video in Latted",
      description:
        "Start with a prompt, add references, choose generation settings, review the generated project, edit the timeline, and export the finished video.",
      totalTime: "PT30M",
      supply: [
        {
          "@type": "HowToSupply",
          name: "Prompt or creative brief",
        },
        {
          "@type": "HowToSupply",
          name: "Optional source attachments",
        },
      ],
      tool: [
        {
          "@type": "HowToTool",
          name: "Latted Composer",
        },
        {
          "@type": "HowToTool",
          name: "Latted timeline editor",
        },
      ],
      step: [
        {
          "@type": "HowToStep",
          name: "Write the brief",
          text: "Describe the video's goal, audience, source material, visual style, pacing, and must-include details.",
        },
        {
          "@type": "HowToStep",
          name: "Attach references",
          text: "Add product screenshots, videos, audio, PDFs, scripts, or brand references that should guide the result.",
        },
        {
          "@type": "HowToStep",
          name: "Choose settings",
          text: "Select aspect ratio and model tier before starting generation.",
        },
        {
          "@type": "HowToStep",
          name: "Review and edit",
          text: "Open the generated project, inspect the storyboard and timeline, adjust keyframes, clips, audio, and overlays.",
        },
        {
          "@type": "HowToStep",
          name: "Export",
          text: "Watch the full timeline, confirm the final review, and export the finished video.",
        },
      ],
    },
  ];

  if (faqItems.length > 0) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems
        .filter((item) => item?.question && item?.answer)
        .map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
    });
  }

  return structuredData;
}

function buildStructuredData(pathname, seo) {
  const data = buildBaseStructuredData(pathname);

  if (seo.pageType === "blog-index") {
    data.push(...buildBlogIndexStructuredData());
  }

  if (seo.pageType === "blog-post" && seo.post) {
    data.push(...buildBlogPostStructuredData(seo.post));
  }

  if (seo.pageType === "docs") {
    data.push(...buildDocsStructuredData());
  }

  return data;
}

function buildStructuredDataDocument(pathname, seo) {
  const graph = buildStructuredData(pathname, seo).map((entry) => {
    const graphEntry = { ...entry };
    delete graphEntry["@context"];
    return graphEntry;
  });

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    const seo = getRouteSeo(pathname);
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath === "/" ? "" : seo.canonicalPath}`;

    document.title = seo.title;

    upsertMeta("description", seo.description);
    upsertMeta("robots", seo.robots);
    upsertMeta("keywords", seo.keywords?.join(", "));
    upsertMeta("author", seo.authorName);

    upsertMeta("og:type", seo.ogType || "website", "property");
    upsertMeta("og:site_name", SITE_NAME, "property");
    upsertMeta("og:title", seo.title, "property");
    upsertMeta("og:description", seo.description, "property");
    upsertMeta("og:url", canonicalUrl, "property");
    upsertMeta("og:image", seo.ogImage || DEFAULT_IMAGE_URL, "property");
    upsertMeta("og:locale", "en_US", "property");
    upsertMeta("article:published_time", seo.publishedAt, "property");
    upsertMeta("article:modified_time", seo.updatedAt, "property");
    upsertMeta("article:author", seo.authorName, "property");

    const existingArticleTags = document.head.querySelectorAll('meta[property="article:tag"]');
    existingArticleTags.forEach((tag) => tag.remove());
    const existingArticleSections = document.head.querySelectorAll('meta[property="article:section"]');
    existingArticleSections.forEach((tag) => tag.remove());

    if (seo.pageType === "blog-post" && seo.post) {
      (seo.post.tags || []).forEach((tag) => {
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", tag);
        document.head.appendChild(el);
      });

      const primarySection = seo.post.tags?.[0];
      if (primarySection) {
        upsertMeta("article:section", primarySection, "property");
      }
    }

    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", seo.title);
    upsertMeta("twitter:description", seo.description);
    upsertMeta("twitter:image", seo.ogImage || DEFAULT_IMAGE_URL);

    upsertLink("canonical", canonicalUrl);

    let structuredDataTag = document.getElementById("latted-structured-data");
    if (seo.includeStructuredData) {
      if (!structuredDataTag) {
        structuredDataTag = document.createElement("script");
        structuredDataTag.type = "application/ld+json";
        structuredDataTag.id = "latted-structured-data";
        document.head.appendChild(structuredDataTag);
      }
      structuredDataTag.textContent = JSON.stringify(
        buildStructuredDataDocument(pathname, seo)
      );
    } else if (structuredDataTag) {
      structuredDataTag.remove();
    }
  }, [location]);

  return null;
}

export default SEOHead;
